/**
 * Ethiopian Calendar Utility Functions
 * Uses kenat library for accurate holiday calculations with Bahire Hasab
 * Based on the Ethiopian Orthodox calendar system
 */

import { getHolidaysForYear, HolidayTags } from "kenat";
import { getFastingPeriod, getFastingInfo } from "kenat";

/**
 * Gregorian to Ethiopian date conversion algorithm
 * @param {Date} gregorianDate - JavaScript Date object (Gregorian calendar)
 * @returns {Object} Ethiopian date object with year, month, day, monthName
 */
export const gregorianToEthiopian = (gregorianDate) => {
  try {
    const gYear = gregorianDate.getFullYear();
    const gMonth = gregorianDate.getMonth() + 1; // 1-12
    const gDay = gregorianDate.getDate();

    // Ethiopian calendar is about 7-8 years behind Gregorian
    // Ethiopian New Year is September 11
    // Before Sept 11: year = gYear - 8
    // From Sept 11 onwards: year = gYear - 7
    let eYear;
    if (gMonth < 9 || (gMonth === 9 && gDay < 11)) {
      eYear = gYear - 8;
    } else {
      eYear = gYear - 7;
    }

    // Calculate the day of the Ethiopian year
    // Start from Ethiopian New Year (Sept 11)
    let ethiopianDayOfYear = 0;

    let isLeap = isGregorianLeapYear(gYear);
    const daysInMonth = [
      31,
      isLeap ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ];

    if (gMonth < 9 || (gMonth === 9 && gDay < 11)) {
      // Before Sept 11: count from Sept 11 of previous year
      // Need to check PREVIOUS year's leap status for Feb calculation
      const prevYear = gYear - 1;
      const isPrevLeap = isGregorianLeapYear(prevYear);
      const prevYearDaysInMonth = [
        31,
        isPrevLeap ? 29 : 28,
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31,
      ];

      // Days remaining in Sept of previous year (11-30) = 20 days
      ethiopianDayOfYear = 20;
      // Add days from Oct to Dec of previous year
      ethiopianDayOfYear += prevYearDaysInMonth[9]; // Oct = 31
      ethiopianDayOfYear += prevYearDaysInMonth[10]; // Nov = 30
      ethiopianDayOfYear += prevYearDaysInMonth[11]; // Dec = 31
      // Add days from Jan to current month of current year
      for (let i = 0; i < gMonth - 1; i++) {
        ethiopianDayOfYear += daysInMonth[i];
      }
      ethiopianDayOfYear += gDay;
    } else if (gMonth === 9) {
      // September from 11th onwards
      // Days from Sept 11 to end of Sept
      ethiopianDayOfYear = gDay - 11 + 1; // Sept 11 is day 1, Sept 30 is day 20
    } else {
      // Oct onwards: count from Sept 11 of current year
      // Days remaining in Sept (11-30) = 20 days
      ethiopianDayOfYear = 20;
      // Add complete months from Oct to current month
      for (let i = 9; i < gMonth - 1; i++) {
        ethiopianDayOfYear += daysInMonth[i];
      }
      ethiopianDayOfYear += gDay;
    }

    // Convert day of year to month and day
    // Each month has 30 days, except the last (Pagume) which has 5 or 6
    let eMonth = Math.ceil(ethiopianDayOfYear / 30);
    let eDay = ethiopianDayOfYear - (eMonth - 1) * 30;

    // Handle edge cases
    if (eDay === 0) {
      eMonth--;
      eDay = 30;
    }

    // Validate month range
    if (eMonth < 1) {
      eMonth = 13;
      eYear--;
    }
    if (eMonth > 13) {
      eMonth = 1;
      eYear++;
    }

    // Validate day range
    if (eMonth === 13) {
      const pagumeDays = isEthiopianLeapYear(eYear) ? 6 : 5;
      if (eDay > pagumeDays) {
        eDay = pagumeDays;
      }
    } else if (eDay > 30) {
      eDay = 30;
    }

    if (eDay < 1) eDay = 1;

    return {
      year: eYear,
      month: eMonth,
      day: eDay,
      monthName: getEthiopianMonthName(eMonth),
      fullDate: `${eDay}/${eMonth}/${eYear}`,
      fullDateWithMonthName: `${eDay} ${getEthiopianMonthName(
        eMonth
      )} ${eYear}`,
    };
  } catch (error) {
    console.error("Error converting to Ethiopian calendar:", error);
    return null;
  }
};

/**
 * Convert Ethiopian date to Gregorian date
 * @param {number} ethYear - Ethiopian year
 * @param {number} ethMonth - Ethiopian month (1-13)
 * @param {number} ethDay - Ethiopian day
 * @returns {Date} Gregorian date
 */
export const ethiopianToGregorian = (ethYear, ethMonth, ethDay) => {
  try {
    // Ethiopian calendar is 7-8 years behind Gregorian
    // Ethiopian year starts on September 11
    // All months in Ethiopian year N map to Gregorian year (N + 7)
    // EXCEPT month 13 (Pagume) which can extend into Gregorian year (N + 8)
    const gYear = ethYear + 7;

    // Create a date starting from Sept 11 of the Gregorian year
    let gDate = new Date(gYear, 8, 11); // September 11 (month 8 in 0-indexed)

    // Calculate days to add from start of Ethiopian year
    const daysToAdd = (ethMonth - 1) * 30 + ethDay - 1;
    gDate.setDate(gDate.getDate() + daysToAdd);

    return gDate;
  } catch (error) {
    console.error("Error converting from Ethiopian calendar:", error);
    return null;
  }
};

/**
 * Get current date in Ethiopian calendar
 * @returns {Object} Current Ethiopian date with all details
 */
export const getCurrentEthiopianDate = () => {
  return gregorianToEthiopian(new Date());
};

/**
 * Get Ethiopian month name in Amharic or English
 * @param {number} month - Month number (1-13)
 * @param {string} language - 'am' for Amharic, 'en' for English (default: 'en')
 * @returns {string} Month name
 */
export const getEthiopianMonthName = (month, language = "en") => {
  const months = {
    en: [
      "Meskerem", // 1
      "Tekemt", // 2
      "Hedar", // 3
      "Tahsas", // 4
      "Ter", // 5
      "Yekatit", // 6
      "Megabit", // 7
      "Miazia", // 8
      "Genbot", // 9
      "Sene", // 10
      "Hamle", // 11
      "Nehasse", // 12
      "Pagume", // 13 (intercalary days)
    ],
    am: [
      "መስከረም", // 1
      "ጤጉምት", // 2
      "ሐዳር", // 3
      "ታሕሳስ", // 4
      "ጠር", // 5
      "የካቲት", // 6
      "መጋቢት", // 7
      "ሚያዝያ", // 8
      "ግንቦት", // 9
      "ሰነ", // 10
      "ሐምሌ", // 11
      "ነሐሴ", // 12
      "ጳጋሜ", // 13
    ],
  };

  const monthsArray = months[language] || months.en;
  return monthsArray[month - 1] || "Unknown";
};

/**
 * Check if a given Gregorian year is a leap year
 * @param {number} gYear - Gregorian year
 * @returns {boolean} True if leap year, false otherwise
 */
export const isGregorianLeapYear = (gYear) => {
  return (gYear % 4 === 0 && gYear % 100 !== 0) || gYear % 400 === 0;
};

/**
 * Check if a given Ethiopian year is a leap year
 * Ethiopian leap year: divisible by 4
 * @param {number} ethYear - Ethiopian year
 * @returns {boolean} True if leap year, false otherwise
 */
export const isEthiopianLeapYear = (ethYear) => {
  return ethYear % 4 === 0;
};

/**
 * Get the number of days in an Ethiopian month
 * @param {number} month - Month number (1-13)
 * @param {number} year - Ethiopian year (needed for Pagume/13th month)
 * @returns {number} Number of days in the month
 */
export const getDaysInEthiopianMonth = (month, year) => {
  if (month < 1 || month > 13) return 0;

  // Months 1-12 have 30 days each
  if (month < 13) return 30;

  // Pagume (13th month) has 5 or 6 days (depending on leap year)
  return isEthiopianLeapYear(year) ? 6 : 5;
};

/**
 * Format Ethiopian date as a readable string
 * @param {Object} ethiopianDate - Ethiopian date object from gregorianToEthiopian
 * @returns {string} Formatted date string with Amharic script
 */
export const formatEthiopianDate = (ethiopianDate) => {
  if (!ethiopianDate) return "";
  return `${ethiopianDate.day} ${ethiopianDate.monthName} ${ethiopianDate.year} ዓ.ም`;
};

/**
 * Get major Ethiopian Orthodox holidays for a given year using kenat library
 * @param {number} ethYear - Ethiopian year
 * @returns {Array} Array of holiday objects with date and name
 */
export const getEthiopianOrthodoxHolidays = (ethYear, language = "en") => {
  try {
    // Determine language param for kenat (kenat expects 'amharic' or 'english')
    const langParam =
      language === "am" || language === "amharic" ? "amharic" : "english";

    // Get Christian holidays from kenat for the given Ethiopian year
    const holidays = getHolidaysForYear(ethYear, {
      filter: [HolidayTags.CHRISTIAN],
      lang: langParam,
    });

    // Optional map of fallback English names (used only if kenat doesn't provide a name)
    const holidayNameMap = {
      enkutatash: "Enkutatash (Ethiopian New Year)",
      meskel: "Meskel (Finding of the True Cross)",
      gena: "Ganna (Ethiopian Christmas)",
      timket: "Timkat (Epiphany)",
      fasika: "Fasika (Easter)",
      nineveh: "Nineveh Fast",
      abiyTsome: "Abiy Tsome (Great Lent)",
      siklet: "Siklet (Ascension Eve)",
      hosanna: "Hosanna (Palm Sunday)",
      debreZeit: "Debre Zeit",
      rikbeKahnat: "Rikbe Kahnat (Apostles' Fast)",
      erget: "Erget",
      paraclete: "Paraclete",
      tsomeHawaryat: "Tsome Hawaryat (Apostles' Fast)",
      tsomeDihnet: "Tsome Dihnet",
      eidFitr: "Eid al-Fitr",
      eidAdha: "Eid al-Adha",
      moulid: "Moulid (Prophet's Birthday)",
    };

    // Transform kenat holidays to our format
    return holidays
      .filter((holiday) => {
        // Only include holidays with valid ethiopian date data
        return (
          holiday &&
          typeof holiday === "object" &&
          holiday.ethiopian &&
          typeof holiday.ethiopian === "object" &&
          typeof holiday.ethiopian.month === "number" &&
          typeof holiday.ethiopian.day === "number"
        );
      })
      .map((holiday) => {
        // Use the name/description returned by kenat (already localized via lang option)
        // Fall back to our English map if kenat does not provide a name
        const nameStr =
          holiday.name || holidayNameMap[holiday.key] || "Holiday";

        // Convert Ethiopian date to Gregorian for display
        const gregorianDate = ethiopianToGregorian(
          holiday.ethiopian.year,
          holiday.ethiopian.month,
          holiday.ethiopian.day
        );

        const gregorianMonth = gregorianDate ? gregorianDate.getMonth() + 1 : 1;
        const gregorianDay = gregorianDate ? gregorianDate.getDate() : 1;

        return {
          ethMonth: holiday.ethiopian.month,
          ethDay: holiday.ethiopian.day,
          name: nameStr,
          gregorianMonth: getGregorianMonth(gregorianMonth),
          gregorianDay: gregorianDay,
          description: holiday.description || "",
          key: holiday.key,
          isMovable: holiday.movable || false,
          tags: Array.isArray(holiday.tags) ? holiday.tags : [],
        };
      });
  } catch (error) {
    console.error("Error fetching holidays from kenat:", error);
    // Fallback to basic holidays if kenat fails
    return getBasicEthiopianHolidays();
  }
};

/**
 * Fallback basic holidays in case kenat fails
 * @returns {Array} Basic holiday array
 */
const getBasicEthiopianHolidays = () => {
  return [
    {
      ethMonth: 1,
      ethDay: 1,
      name: "Enkutatash (Ethiopian New Year)",
      gregorianMonth: "September",
      gregorianDay: 11,
      description:
        "The Ethiopian New Year celebration, marking the end of the rainy season",
      key: "enkutatash",
      isMovable: false,
      tags: ["public"],
    },
    {
      ethMonth: 3,
      ethDay: 17,
      name: "Meskel (Finding of the True Cross)",
      gregorianMonth: "September",
      gregorianDay: 27,
      description: "Celebration of the discovery of the True Cross",
      key: "meskel",
      isMovable: false,
      tags: ["public", "religious"],
    },
    {
      ethMonth: 4,
      ethDay: 11,
      name: "Timkat (Epiphany)",
      gregorianMonth: "January",
      gregorianDay: 19,
      description: "Commemoration of the baptism of Jesus Christ",
      key: "timkat",
      isMovable: false,
      tags: ["public", "religious"],
    },
    {
      ethMonth: 4,
      ethDay: 29,
      name: "Ganna (Ethiopian Christmas)",
      gregorianMonth: "January",
      gregorianDay: 7,
      description: "Celebration of the birth of Jesus Christ",
      key: "ganna",
      isMovable: false,
      tags: ["public", "religious"],
    },
  ];
};

/**
 * Get fasting days for the Ethiopian Orthodox Church
 * Uses kenat library's comprehensive fasting functions and Bahire Hasab calculations
 * All fasting periods are calculated dynamically via kenat (no hardcoded values)
 * @param {number} ethYear - Ethiopian year
 * @param {string} language - Language for fasting period names ("en", "english", "am", or "amharic")
 * @returns {Object} Object with fastingPeriods array and fasikaDate
 */
export const getEthiopianFastingDays = (ethYear, language = "en") => {
  try {
    // Determine language param for kenat (kenat expects 'amharic' or 'english')
    const langParam =
      language === "am" || language === "amharic" ? "amharic" : "english";

    // List of all Christian fasting periods supported by kenat
    const kenatFastKeys = [
      "NINEVEH", // Movable: 3-day fast before Great Lent
      "ABIY_TSOME", // Movable: 56-day Great Lent
      "TSOME_HAWARYAT", // Movable: Apostles' Fast after Pentecost
      "TSOME_NEBIYAT", // Fixed: Hidar 15 - Tahsas 28 (Prophets' Fast)
      "FILSETA", // Fixed: Nehase 1-14 (Assumption Fast)
      "TSOME_DIHENET", // Weekly: Wednesdays & Fridays (except after Easter)
    ];

    const fastingPeriods = [];
    let fasikaDate = null;

    // Get fasting info and periods from kenat for each fast type
    for (const fastKey of kenatFastKeys) {
      const fastInfo = getFastingInfo(fastKey, ethYear, { lang: langParam });

      if (!fastInfo) continue;

      // Get the fasting period (start and end dates)
      const period = getFastingPeriod(fastKey, ethYear);

      if (!period) {
        // TSOME_DIHENET (Wed/Fri fast) doesn't have a single contiguous period
        if (fastKey === "TSOME_DIHENET") {
          fastingPeriods.push({
            key: fastKey,
            name: fastInfo.name,
            description: fastInfo.description,
            startMonth: null, // No fixed period for weekly fast
            startDay: null,
            endMonth: null,
            endDay: null,
            isMovable: false,
            isWeeklyFast: true,
          });
        }
        continue;
      }

      // Convert period dates to our format
      fastingPeriods.push({
        key: fastKey,
        name: fastInfo.name,
        description: fastInfo.description,
        startMonth: period.start.month,
        startDay: period.start.day,
        endMonth: period.end.month,
        endDay: period.end.day,
        isMovable: ["NINEVEH", "ABIY_TSOME", "TSOME_HAWARYAT"].includes(
          fastKey
        ),
        isWeeklyFast: false,
      });
    }

    // Get Fasika date from holidays for reference
    const holidays = getHolidaysForYear(ethYear, {
      filter: [HolidayTags.CHRISTIAN],
    });
    const fasika = holidays.find((h) => h.key === "fasika");
    if (fasika) {
      fasikaDate = {
        month: fasika.ethiopian.month,
        day: fasika.ethiopian.day,
        year: fasika.ethiopian.year,
      };
    }

    return {
      fastingPeriods,
      fasikaDate,
    };
  } catch (error) {
    console.error("Error calculating fasting days from kenat:", error);
    // Return empty array if kenat fails
    return {
      fastingPeriods: [],
      fasikaDate: null,
    };
  }
};

/**
 * Check if a given Ethiopian date is a fasting day
 * Handles both period-based fasts and weekly fasts (Wed/Fri)
 * @param {number} ethMonth - Ethiopian month (1-13)
 * @param {number} ethDay - Ethiopian day
 * @param {number} ethYear - Ethiopian year
 * @param {string} language - Language for fasting period names ("en", "english", "am", or "amharic")
 * @returns {Object} Fasting period info or null
 */
export const getFastingPeriodForDate = (
  ethMonth,
  ethDay,
  ethYear,
  language = "en"
) => {
  const { fastingPeriods, fasikaDate } = getEthiopianFastingDays(
    ethYear,
    language
  );

  // Check all fasting periods
  for (const period of fastingPeriods) {
    // Handle weekly fasts (Tsome Dihnet - Wed/Fri)
    if (period.isWeeklyFast) {
      // Need to convert Ethiopian date to Gregorian to check day of week
      const gregorianDate = ethiopianToGregorian(ethYear, ethMonth, ethDay);
      const dayOfWeek = gregorianDate.getDay(); // 0=Sun, 3=Wed, 5=Fri

      // Check if it's Wednesday (3) or Friday (5)
      const isWedOrFri = dayOfWeek === 3 || dayOfWeek === 5;

      if (isWedOrFri && fasikaDate) {
        // Exception: no fasting Wed/Fri for 50 days after Easter (Fasika)
        const daysFromFasika =
          (ethMonth - fasikaDate.month) * 30 + (ethDay - fasikaDate.day);
        const isInEasterSeason = daysFromFasika > 0 && daysFromFasika <= 50;

        if (!isInEasterSeason) {
          return period;
        }
      } else if (isWedOrFri) {
        return period;
      }
      continue;
    }

    // Handle period-based fasts
    if (!period.startMonth || !period.endMonth) continue;

    if (period.startMonth === period.endMonth) {
      // Period within same month
      if (
        ethMonth === period.startMonth &&
        ethDay >= period.startDay &&
        ethDay <= period.endDay
      ) {
        return period;
      }
    } else {
      // Period spans multiple months
      const isInPeriod =
        (ethMonth === period.startMonth && ethDay >= period.startDay) ||
        (ethMonth === period.endMonth && ethDay <= period.endDay) ||
        (ethMonth > period.startMonth && ethMonth < period.endMonth);

      if (isInPeriod) {
        return period;
      }
    }
  }

  return null;
};

/**
 * Convert month number to month name
 * @param {number} month - Month number (1-12)
 * @returns {string} Month name
 */
const getGregorianMonth = (month) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1] || "Unknown";
};

export default {
  gregorianToEthiopian,
  ethiopianToGregorian,
  getCurrentEthiopianDate,
  getEthiopianMonthName,
  isEthiopianLeapYear,
  isGregorianLeapYear,
  getDaysInEthiopianMonth,
  formatEthiopianDate,
  getEthiopianOrthodoxHolidays,
  getEthiopianFastingDays,
  getFastingPeriodForDate,
};
